/**
 * SigV4 request signing for API Gateway calls.
 * Uses @smithy/signature-v4 to sign HTTP requests with AWS credentials.
 */
import { SignatureV4 } from '@smithy/signature-v4';
import { HttpRequest } from '@smithy/protocol-http';
import { Sha256 } from '@aws-crypto/sha256-js';
import { getCredentials } from './midwayAuth';

const REGION = 'us-west-2';
const SERVICE = 'execute-api';

/**
 * Make a SigV4-signed fetch request to the API Gateway.
 */
export async function signedFetch(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  const credentials = await getCredentials();

  const parsedUrl = new URL(url);
  const headers: Record<string, string> = {
    host: parsedUrl.hostname,
    'content-type': 'application/json',
  };

  // Add any existing headers
  if (options.headers) {
    const h = options.headers as Record<string, string>;
    Object.keys(h).forEach(k => { headers[k.toLowerCase()] = h[k]; });
  }

  const request = new HttpRequest({
    method: (options.method || 'GET').toUpperCase(),
    protocol: parsedUrl.protocol,
    hostname: parsedUrl.hostname,
    port: parsedUrl.port ? parseInt(parsedUrl.port) : undefined,
    path: parsedUrl.pathname,
    query: (() => {
      const q: Record<string, string> = {};
      parsedUrl.searchParams.forEach((v, k) => { q[k] = v; });
      return q;
    })(),
    headers,
    body: options.body as string || undefined,
  });

  const signer = new SignatureV4({
    service: SERVICE,
    region: REGION,
    credentials: {
      accessKeyId: credentials.accessKeyId,
      secretAccessKey: credentials.secretAccessKey,
      sessionToken: credentials.sessionToken,
    },
    sha256: Sha256,
  });

  const signedRequest = await signer.sign(request);

  // Convert signed request back to fetch options
  const signedHeaders: Record<string, string> = {};
  Object.entries(signedRequest.headers).forEach(([key, value]) => {
    // Skip host header (browser sets it automatically)
    if (key.toLowerCase() !== 'host') {
      signedHeaders[key] = value;
    }
  });

  return fetch(url, {
    method: signedRequest.method,
    headers: signedHeaders,
    body: signedRequest.body || undefined,
  });
}
